import {AgentNs, type ProjectDocumentNs} from '@shared/types';
import {canRequestDocumentAnalysis, isDocumentAnalyzed} from '@shared/helpers/project-document-status.helper';

// ─── NEXT ACTIONS DEFINITIONS ─────────────────────────────────
// Ported from ms-chat-agent POC (`public/js/next-actions.js`).
// Keep in sync with the backend tag vocabulary.

type StaticDef = AgentNs.NaDefinition;
type DynamicDef = (params: Record<string, string>) => AgentNs.NaDefinition;

// Note: the Sources button was merged with Overview into a single "/context"
// entry. The Context Inspector now has tabs (Overview, Sources) instead of
// two separate NA buttons. See contextRow rendering in flattenNaDefinition +
// ms-chat-context-inspector-card.vue.

const NA_DEFINITIONS: Record<string, StaticDef | DynamicDef> = {
  EMPTY: {
    groups: [
      {
        id: 'get_started',
        buttons: [
          { id: 'upload_document',     name: 'Upload Document',  subtext: 'Meeting transcript, spec, or notes', command: 'open_file_picker'  },
          { id: 'enter_project_input', name: 'Describe Project', subtext: 'Type the idea in a few sentences',   command: 'focus_chat_input' },
        ],
      },
    ],
  },

  GENERATE: (params) => {
    const confidence = params.CONFIDENCE || '35';
    const confNum = parseInt(confidence, 10) || 0;
    const enrichMaxed = confNum >= 90;
    return {
      groups: [
        {
          id: 'generate_builder',
          buttons: [
            { id: 'only_modules',     name: 'Generate Modules',               subtext: `Generate Modules first, features later · ~${confidence}% confidence`, command: 'trigger_builder_modules'          },
            { id: 'modules_features', name: 'Generate Modules with Features', subtext: `Full generation in one step · ~${confidence}% confidence`,           command: 'trigger_builder_modules_features' },
          ],
        },
        {
          id: 'discovery',
          buttons: [
            {
              id:       'discovery',
              name:     'Discovery',
              subtext:  enrichMaxed ? 'Over 90% reached — no further enrichment needed' : 'Answer a few questions to deepen the project · up to ~90% confidence',
              command:  'trigger_enrich_context',
              disabled: enrichMaxed,
              submenu:  true,
            },
          ],
        },
        { separator: true },
        { contextRow: true, title: 'Context' },
      ],
    };
  },

  BUILDER_INSERTED: (params) => {
    const genType = params.GENTYPE === 'modules_features' ? 'modules_features' : 'modules';
    const groups: AgentNs.NaGroup[] = [];

    // No "Update Modules" button here — right after Insert there is no new
    // context that would justify an update. The REFINE NA (emitted by the
    // backend ANSWER agent) takes over the update scenario once new context
    // arrives.

    // "Generate Features" — only when modules were generated without features.
    if (genType === 'modules') {
      groups.push({
        id: 'features_action',
        buttons: [
          { id: 'generate_features', name: 'Generate Features', subtext: 'Add features to modules', command: 'builder_generate_features' },
        ],
      });
    }

    const artifactBtns: AgentNs.NaButton[] = [];
    if (genType === 'modules_features') {
      artifactBtns.push({ id: 'generate_features',     name: 'Generate Features',     subtext: 'Add features to modules',         command: 'coming_soon', disabled: true });
      artifactBtns.push({ id: 'generate_subfeatures',  name: 'Generate Subfeatures',  subtext: 'Add subfeatures to features',     command: 'builder_generate_subfeatures' });
    }
    artifactBtns.push({ id: 'generate_descriptions', name: 'Generate Descriptions', subtext: 'Generate long descriptions',    command: 'coming_soon', disabled: true });
    artifactBtns.push({ id: 'generate_user_stories', name: 'Generate User Stories', subtext: 'Generate user stories with AC', command: 'coming_soon', disabled: true });
    artifactBtns.push({ id: 'generate_estimation',   name: 'Generate Estimation',   subtext: 'Generate effort estimation',    command: 'coming_soon', disabled: true });

    groups.push({ id: 'artifacts', title: 'For Modules/Features', buttons: artifactBtns });
    groups.push({
      id: 'discovery',
      buttons: [
        { id: 'discovery', name: 'Discovery', subtext: 'Answer a few questions to deepen the project · up to ~90% confidence', command: 'trigger_enrich_context', submenu: true },
      ],
    });
    groups.push({ separator: true });
    groups.push({ contextRow: true, title: 'Context' });

    return { groups };
  },

  CONFLICT: {
    groups: [
      {
        id: 'project_conflict',
        buttons: [
          { id: 'conflict_proceed', name: 'Proceed', subtext: 'Add the new input anyway', command: 'conflict_proceed' },
          { id: 'conflict_discard', name: 'Discard', subtext: "Don't add the new input", command: 'conflict_discard' },
        ],
      },
    ],
  },

  // Document Filename Conflict — fired when the user picks a file whose name
  // already exists in the library. 3 buttons: re-analyze, use-existing,
  // discard. The FILENAME parameter from the tag is shown as the subtitle.
  DOC_CONFLICT: (_params) => {
    return {
      groups: [
        {
          id: 'doc_conflict_title',
          title: 'This document already exists in your project',
          buttons: [
            { id: 'show_analysis',  name: 'Show Analysis',  subtext: 'View captured topics, decisions, entities of the existing version', command: 'doc_conflict_show_analysis' },
            { id: 'analyze_anyway', name: 'Analyze Anyway',                  subtext: 'Re-analyze as a new entry — old version stays',                  command: 'doc_conflict_proceed' },
            { id: 'discard',        name: 'Discard',                         subtext: "Cancel — don't add this file",                                   command: 'doc_conflict_discard' },
          ],
        },
      ],
    };
  },

  BUILDER_CARD: (params) => {
    const openPointsCount = parseInt(params.OPEN_POINTS || '0', 10);
    const genType = params.GENTYPE || 'modules';
    const confNum = parseInt(params.CONFIDENCE || '0', 10) || 0;
    const enrichMaxed = confNum >= 90;
    const isDiff = params.DIFF === '1';
    const insertSubtext = isDiff
      ? 'Merge changes into project'
      : (openPointsCount > 0 ? 'Insert with open points into project' : 'Insert into project');
    const discardSubtext = isDiff ? 'Discard changes and keep current tree' : 'Discard and go back to chat';

    const structureBtns: AgentNs.NaButton[] = [
      isDiff
        ? { id: 'insert_merge', name: 'Insert & Merge', subtext: insertSubtext, command: 'builder_insert' }
        : { id: 'insert',       name: 'Insert',         subtext: insertSubtext, command: 'builder_insert' },
    ];
    if (!isDiff && genType === 'modules') {
      structureBtns.push({ id: 'generate_features', name: 'Generate Features', subtext: 'Generate features for current modules', command: 'builder_generate_features' });
    }
    if (openPointsCount > 0) {
      structureBtns.push({ id: 'resolve_open_points', name: 'Resolve Open Points', subtext: `Resolve ${openPointsCount} open point${openPointsCount > 1 ? 's' : ''}`, command: 'builder_resolve' });
    }

    return {
      groups: [
        // Action group first: insert / (generate-features) / (resolve-open-points)
        { id: 'structure', buttons: structureBtns },
        // Discovery is also an action — user-initiated drill-down
        {
          id: 'discovery',
          buttons: [
            {
              id:       'discovery',
              name:     'Discovery',
              subtext:  enrichMaxed ? 'Over 90% reached — no further enrichment needed' : 'Answer a few questions to deepen the project · up to ~90% confidence',
              command:  'builder_enrich',
              disabled: enrichMaxed,
              submenu:  true,
            },
          ],
        },
        // Discard sits with the action cluster — it's also a user choice.
        {
          id: 'exit',
          buttons: [{ id: 'discard', name: 'Discard', subtext: discardSubtext, command: 'builder_discard' }],
        },
        // Context is informational, not an action — push it to the bottom.
        { separator: true },
        { contextRow: true, title: 'Context' },
      ],
    };
  },

  ENRICH_DEPTH: {
    groups: [
      {
        id: 'depth_options',
        title: 'Discovery',
        buttons: [
          { id: 'quick',  name: 'Quick',  subtext: '3-4 Questions · +15% confidence',   command: 'trigger_enrich_depth_quick'  },
          { id: 'medium', name: 'Medium', subtext: '6-8 Questions · +25% confidence',   command: 'trigger_enrich_depth_medium' },
          { id: 'deep',   name: 'Deep',   subtext: '10-14 Questions · +40% confidence', command: 'trigger_enrich_depth_deep'   },
        ],
      },
      { separator: true },
      {
        id: 'back',
        buttons: [
          { id: 'back', name: 'Back', subtext: 'Back to menu', command: 'trigger_enrich_depth_back', back: true },
        ],
      },
    ],
  },

  // CLARIFY `generate_without_type` mode emits this tag. Backend Audit
  // Task 9: the old name BUILDER_TYPE was removed; GENERATE_TYPE is the
  // only active name (the orchestrator's NA_GENERATE_TYPE regex matches
  // only this one). See AgentOrchestrator.java lines 63-69.
  GENERATE_TYPE: (params) => {
    const confidence = params.DIRECT || '35';
    return {
      groups: [
        {
          id: 'generate_builder',
          buttons: [
            { id: 'only_modules',     name: 'Only Modules',       subtext: `Generate modules first, features later · ~${confidence}% confidence`, command: 'trigger_builder_modules'          },
            { id: 'modules_features', name: 'Modules + Features', subtext: `Full generation in one step · ~${confidence}% confidence`,           command: 'trigger_builder_modules_features' },
          ],
        },
      ],
    };
  },

  // CLARIFY `intent_unclear` → two intent buckets the user picks between.
  // "Analyze" routes to ANALYZE (user contributes project info); "Ask"
  // routes to ANSWER (user wants to query the project).
  INTENT_PICKER: {
    groups: [
      {
        id: 'intent_buckets',
        title: 'What would you like to do?',
        buttons: [
          { id: 'analyze',  name: 'Analyze', subtext: 'Describe or upload project info', command: 'trigger_intent_analyze' },
          { id: 'ask',      name: 'Ask',     subtext: 'Ask about the current project',   command: 'trigger_intent_ask' },
          { id: 'discard',  name: 'Discard', subtext: 'Remove attachment(s) — no analysis', command: 'trigger_discard_attachment' },
        ],
      },
    ],
  },

  // Frontend override: all picked docs are ALREADY ANALYZED (no raw, no new
  // files). Re-analysis would be a waste of tokens — instead we offer
  // "Show Analysis" (opens the inline Sources card with the doc highlighted)
  // and "Ask" (focuses the composer for free-form typing).
  ANALYZED_DOC_PICKER: {
    groups: [
      {
        id: 'analyzed_picker',
        title: 'This document is already analyzed',
        buttons: [
          { id: 'show',    name: 'Show Analysis', subtext: 'View captured topics, decisions, entities', command: 'trigger_show_analysis' },
          { id: 'ask',     name: 'Ask',           subtext: 'Type your question about this document',    command: 'trigger_intent_ask' },
          { id: 'discard', name: 'Discard',       subtext: 'Remove attachment(s) — no action',           command: 'trigger_discard_attachment' },
        ],
      },
    ],
  },

  // Frontend override: mix of analyzed + raw/new docs. The user decides what
  // to prioritize — Show (opens the card for the analyzed one), Analyze
  // (processes the raw ones — a frontend filter ensures analyzed docs are
  // not re-analyzed), Ask (focuses the composer), or Discard (removes all
  // attachments).
  MIXED_DOC_PICKER: {
    groups: [
      {
        id: 'mixed_picker',
        title: 'Mix of analyzed and new documents',
        buttons: [
          { id: 'show',    name: 'Show Analysis', subtext: 'View the existing analysis',                          command: 'trigger_show_analysis' },
          { id: 'analyze', name: 'Analyze',       subtext: 'Process raw document(s) — existing stays referenced', command: 'trigger_intent_analyze' },
          { id: 'ask',     name: 'Ask',           subtext: 'Type your question',                                  command: 'trigger_intent_ask' },
          { id: 'discard', name: 'Discard',       subtext: 'Remove attachment(s) — no action',                    command: 'trigger_discard_attachment' },
        ],
      },
    ],
  },

  // Frontend override: every picked doc is still being processed (pending /
  // analyzing), superseded, or in a status this client does not know. None
  // of them may be (re)analyzed, so Analyze is deliberately absent — only
  // Ask (free-form typing) and Discard remain. Neutral by design: an unknown
  // future state must never be treated as actionable.
  ACTIVE_DOC_PICKER: {
    groups: [
      {
        id: 'active_picker',
        title: 'This document is still being processed',
        buttons: [
          { id: 'ask',     name: 'Ask',     subtext: 'Type your question about the project', command: 'trigger_intent_ask' },
          { id: 'discard', name: 'Discard', subtext: 'Remove attachment(s) — no action',      command: 'trigger_discard_attachment' },
        ],
      },
    ],
  },

  // ANSWER post-insert: same shape as GENERATE but the primary verb is
  // "Refine" — the user already has a structure, they're improving it.
  REFINE: (params) => {
    const confidence = params.CONFIDENCE || '35';
    const confNum = parseInt(confidence, 10) || 0;
    const enrichMaxed = confNum >= 90;
    return {
      groups: [
        {
          id: 'refine_builder',
          buttons: [
            { id: 'regenerate', name: 'Update Structure', subtext: `Update structure with new context · ~${confidence}% confidence`, command: 'trigger_builder_regenerate' },
          ],
        },
        {
          id: 'discovery',
          buttons: [
            {
              id:       'discovery',
              name:     'Discovery',
              subtext:  enrichMaxed ? 'Over 90% reached — no further enrichment needed' : 'Answer a few questions to deepen the project · up to ~90% confidence',
              command:  'trigger_enrich_context',
              disabled: enrichMaxed,
              submenu:  true,
            },
          ],
        },
        { separator: true },
        { contextRow: true, title: 'Context' },
      ],
    };
  },
};

// ─── COMMAND → LLM TEXT MAP ───────────────────────────────────
// When a command is triggered, this is the user-facing text sent
// to the chat endpoint. `null` means the command is handled purely
// on the client (builder insert/discard/resolve/enrich, etc.).

export const COMMAND_TO_TEXT: Record<string, string | null> = {
  trigger_builder_modules:          'Generate Modules',
  trigger_builder_modules_features: 'Generate Modules & Features',
  trigger_builder_regenerate:       'Regenerate',
  trigger_enrich_context:           'Discovery',
  trigger_enrich_depth_quick:       'Quick Enrich',
  trigger_enrich_depth_medium:      'Medium Enrich',
  trigger_enrich_depth_deep:        'Deep Enrich',
  trigger_enrich_depth_back:        'Back',
  // CLARIFY intent_unclear — user explicitly picks which bucket to enter.
  // The text becomes the next user_input so INTENT_ROUTER classifies straight to
  // the right agent without going through CLARIFY again.
  trigger_intent_analyze:           'Analyze',
  // "Ask" is a pure client action: closes the NA bar and focuses the chat
  // input. NO user bubble — the user is expected to type their question.
  trigger_intent_ask:               null,
  // Conflict resolution is handled client-side in dispatchCommand — no LLM text.
  conflict_proceed:                 null,
  conflict_discard:                 null,
  doc_conflict_proceed:             null,
  doc_conflict_show_analysis:       null,
  doc_conflict_discard:             null,
  // Picker-aware: all picked docs are analyzed → opens the inline Sources
  // card with highlight, NO re-analyze.
  trigger_show_analysis:            null,
  // Frontend-only: removes all pending attachments (pendingFiles +
  // pendingDocumentKeys), NA back to normal state.
  trigger_discard_attachment:       null,
  builder_insert:                   null,
  builder_resolve:                  null,
  builder_enrich:                   null,
  builder_discard:                  null,
  builder_generate_features:        null,
  builder_generate_subfeatures:     null,
  // "Context" Inspector card — frontend-only, no backend call. Opens an
  // inline card with tabs (Overview, Sources) after the last message.
  open_context_card:                null,
  // Legacy aliases — mapped to open_context_card, kept here only so older
  // dispatcher code (e.g. trigger_show_analysis) keeps working.
  trigger_context_sources:          null,
  open_context_panel:               null,
  open_file_picker:                 null,
  focus_chat_input:                 null,
  coming_soon:                      null,
};

/**
 * Direct routing: for these commands the frontend already knows which agent
 * should run (the user explicitly clicked `/generate-modules` etc.). With
 * `actionHintOverride` the backend skips the INTENT_ROUTER LLM call and goes
 * straight to the target agent. Saves ~1-2s + token cost per click.
 *
 * If a command is NOT listed here → no override → INTENT_ROUTER classifies.
 */
export const COMMAND_TO_ACTION_HINT: Record<string, string> = {
  trigger_builder_modules:          'BUILDER:generate_modules',
  // Backend variant is `generate_modules_features` (no "with") — see
  // BuilderAgent.java handle() switch. Earlier "with_features" was a typo
  // that triggered "Variant bulunamadı" errors.
  trigger_builder_modules_features: 'BUILDER:generate_modules_features',
  trigger_builder_regenerate:       'BUILDER:regenerate',
};

// ─── SLUG / FILTER HELPERS ─────────────────────────────────────

// Converts a button's display name into the `/command-slug` shown in the NA
// bar and used as the filter target (matches POC's `cmdName` in ui.js).
export function slugify(name: string): string {
  return '/' + (name || '')
    .toLowerCase()
    .replace(/\+/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// A flat row in the rendered NA bar.
export type NaFlatItem =
  | { kind: 'separator'; hidden: boolean }
  | { kind: 'title'; text: string; hidden: boolean }
  | { kind: 'context'; command: string; hidden: boolean; btnIndex: number }
  | { kind: 'button'; button: AgentNs.NaButton; slug: string; hidden: boolean; btnIndex: number };

// Flatten a definition into an ordered row list, applying filter-based
// visibility (titles hide if all buttons hidden, separators hide if they
// have no visible neighbour on one side).
export function flattenNaDefinition(
  def: AgentNs.NaDefinition | null,
  filter = '',
): NaFlatItem[] {
  if (!def) return [];
  const items: NaFlatItem[] = [];
  const lc = filter.toLowerCase();
  let btnIndex = 0;

  const passes = (slug: string) => !lc || slug.includes(lc);

  def.groups.forEach(group => {
    if (group.separator) {
      items.push({kind: 'separator', hidden: false});
      return;
    }
    if (group.contextRow) {
      const slug = '/context';
      const hidden = !passes(slug);
      // Optional section-header (e.g. "Context") over the progress row.
      if (group.title) {
        items.push({kind: 'title', text: group.title, hidden});
      }
      items.push({kind: 'context', command: 'open_context_card', hidden, btnIndex: btnIndex++});
      return;
    }
    if (!group.buttons?.length) return;

    const buttons = group.buttons;
    const titleIdx = items.length;
    if (group.title) {
      items.push({kind: 'title', text: group.title, hidden: false});
    }
    buttons.forEach(btn => {
      const slug = slugify(btn.name);
      items.push({kind: 'button', button: btn, slug, hidden: !passes(slug), btnIndex: btnIndex++});
    });
    if (group.title) {
      const allHidden = buttons.every(b => !passes(slugify(b.name)));
      (items[titleIdx] as NaFlatItem & {hidden: boolean}).hidden = allHidden;
    }
  });

  // Hide separators that are leading / trailing / between empty groups.
  items.forEach((item, i) => {
    if (item.kind !== 'separator') return;
    (item as NaFlatItem & {hidden: boolean}).hidden =
      !(_scanVisible(items, i - 1, -1) && _scanVisible(items, i + 1, 1));
  });

  return items;
}

function _scanVisible(items: NaFlatItem[], start: number, step: number): boolean {
  for (let j = start; j >= 0 && j < items.length; j += step) {
    const c = items[j];
    if (!c) continue;
    if (c.kind === 'separator') return false;
    if ((c.kind === 'button' || c.kind === 'context') && !c.hidden) return true;
  }
  return false;
}

// Returns the flat items that are visible AND clickable (button or context).
// Used by the keyboard-nav index → mapping into the flat list.
export function visibleActionableItems(items: NaFlatItem[]): NaFlatItem[] {
  return items.filter(i => (i.kind === 'button' || i.kind === 'context') && !i.hidden);
}

// ─── ATTACHMENT-AWARE PICKER TAG ──────────────────────────────
// Content-aware NA override for the pending attachment mix. Status is the
// server-derived `analysisStatus` (via the shared status helper); the presence
// of `contentSummary` is never a state signal.
//   - Mix of analyzed + raw/new           → MIXED_DOC_PICKER (/show + /analyze + /ask)
//   - Only ANALYZED docs                  → ANALYZED_DOC_PICKER (/show-analysis + /ask)
//   - Any raw (CONVERTED/FAILED/legacy) or a new file → INTENT_PICKER (/analyze + /ask)
//   - Only active/superseded/unknown docs → ACTIVE_DOC_PICKER (/ask + /discard; no Analyze)
export function resolveAttachmentPickerTag(
  pendingDocumentKeys: readonly string[],
  documents: readonly ProjectDocumentNs.ProjectDocument[],
  hasNewFile: boolean,
): string {
  const findDoc = (key: string) => documents.find(x => x.documentKey === key);
  const hasAnalyzedKey = pendingDocumentKeys.some(k => isDocumentAnalyzed(findDoc(k)));
  const hasRawKey      = pendingDocumentKeys.some(k => canRequestDocumentAnalysis(findDoc(k)));
  const hasRawOrNew    = hasNewFile || hasRawKey;

  if (hasAnalyzedKey && hasRawOrNew) return '[NA:MIXED_DOC_PICKER]';
  if (hasAnalyzedKey)                return '[NA:ANALYZED_DOC_PICKER]';
  if (hasRawOrNew)                   return '[NA:INTENT_PICKER]';
  return '[NA:ACTIVE_DOC_PICKER]';
}

// ─── TAG RESOLVER ─────────────────────────────────────────────

export function resolveNextActions(tag: string | null | undefined): AgentNs.NaDefinition | null {
  if (!tag || typeof tag !== 'string') return null;

  const inner = tag.replace(/^\[|\]$/g, '');
  const parts = inner.split('|');
  const tagName = (parts[0] ?? '').replace('NA:', '');

  const params: Record<string, string> = {};
  parts.slice(1).forEach(p => {
    const colonIdx = p.indexOf(':');
    if (colonIdx < 0) return;
    const k = p.slice(0, colonIdx);
    const v = p.slice(colonIdx + 1);
    if (k) params[k] = v;
  });

  const def = NA_DEFINITIONS[tagName];
  if (!def) {
    console.warn(`[NA] Unknown tag: ${tagName}`);
    return null;
  }

  return typeof def === 'function' ? def(params) : def;
}
